/* useRenderTier — decides which render ladder a visitor gets.

   Three tiers, in priority order:
     STATIC     prefers-reduced-motion, no WebGL, low battery, save-data,
                or the user has forced STATIC via the footer toggle.
     LITE       WebGL present but device is memory-constrained or mobile,
                or WebGL2 unsupported.  Renders pre-baked stills + CSS motion.
     CINEMATIC  WebGL2 + desktop-class hints.  Renders the full R3F scenes.

   The tier can auto-downgrade at runtime: <CinematicShell /> monitors FPS
   and dispatches `dt6:auto-downgrade` when FPS drops below 30 for ≥3s.
*/
import { useEffect, useState } from "react";
import { getMotionPref } from "./motionPref";

// One-shot probe — cached after the first call so multiple mounted scenes
// don't spin up throwaway WebGL contexts.
let _cachedProbe = null;

function probeCapabilities() {
  if (_cachedProbe) return _cachedProbe;

  if (typeof window === "undefined") {
    _cachedProbe = { webgl2: false, webgl1: false, saveData: false, lowMem: true, reducedMotion: true };
    return _cachedProbe;
  }
  let webgl2 = false, webgl1 = false;
  try {
    const c = document.createElement("canvas");
    webgl2 = !!c.getContext("webgl2");
    if (!webgl2) webgl1 = !!(c.getContext("webgl") || c.getContext("experimental-webgl"));
  } catch (_e) { /* no gl */ }

  const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  const saveData = !!(conn && conn.saveData);
  const slowNet = !!(conn && ["slow-2g", "2g"].includes(conn.effectiveType || ""));

  // deviceMemory is in GB; missing on iOS.  Fall back to hardwareConcurrency.
  const mem = typeof navigator.deviceMemory === "number" ? navigator.deviceMemory : null;
  const cpu = typeof navigator.hardwareConcurrency === "number" ? navigator.hardwareConcurrency : null;
  const lowMem = (mem !== null && mem <= 3) || (cpu !== null && cpu <= 2);

  const reducedMotion =
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // Coarse mobile hint — used only to bias LITE, not as a hard exclusion.
  const coarsePointer =
    typeof window.matchMedia === "function" &&
    window.matchMedia("(pointer: coarse)").matches;

  _cachedProbe = { webgl2, webgl1, saveData, slowNet, lowMem, reducedMotion, coarsePointer };
  return _cachedProbe;
}

export function detectTier() {
  const forced = getMotionPref();
  if (forced === "static") return "static";
  const p = probeCapabilities();
  if (p.reducedMotion || p.saveData || p.slowNet) return "static";
  if (!p.webgl2 && !p.webgl1) return "static";
  if (forced === "cinematic") return "cinematic";
  if (!p.webgl2 || p.lowMem || p.coarsePointer) return "lite";
  return "cinematic";
}

export default function useRenderTier() {
  const [tier, setTier] = useState(() => (typeof window === "undefined" ? "static" : detectTier()));

  useEffect(() => {
    setTier(detectTier());
    const rerun = () => setTier(detectTier());
    const onDowngrade = () => setTier((t) => (t === "cinematic" ? "lite" : t));
    window.addEventListener("dt6:motion-pref-change", rerun);
    window.addEventListener("dt6:auto-downgrade", onDowngrade);
    // Also react to reduced-motion toggling in the OS.
    let mql;
    if (typeof window.matchMedia === "function") {
      mql = window.matchMedia("(prefers-reduced-motion: reduce)");
      if (mql.addEventListener) mql.addEventListener("change", rerun);
      else if (mql.addListener) mql.addListener(rerun);
    }
    return () => {
      window.removeEventListener("dt6:motion-pref-change", rerun);
      window.removeEventListener("dt6:auto-downgrade", onDowngrade);
      if (mql) {
        if (mql.removeEventListener) mql.removeEventListener("change", rerun);
        else if (mql.removeListener) mql.removeListener(rerun);
      }
    };
  }, []);

  return tier;
}
